














import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import moment from "moment";
import _ from "lodash";
import InvestmentsPerformanceLineChart from "@/shared/components/charts/InvestmentsPerformanceLineChart.vue";
import PocGLineChart from "@/shared/components/charts/PocGLineChart.vue";
import {
  GradientPoint,
  InvestmentCustodian,
  ChartDataItem,
  DrivewealthAccountIdentifierArgs
} from "@/shared/types";
import { useInvestmentDailyBalanceStore } from "@/shared/store/investmentDailyBalance";
import { useThemeStore } from "@/shared/store/theme";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import { useDrivewealthAccountPerformanceStore } from "@/shared/store/drivewealthAccountPerformance";

@Component({
  components: {
    InvestmentsPerformanceLineChart,
    PocGLineChart
  }
})
export default class InvestmentLineChart extends Vue {
  drivewealthStore = useDrivewealthStore();
  drivewealthAccountPerformanceStore = useDrivewealthAccountPerformanceStore();
  investmentDailyBalanceStore = useInvestmentDailyBalanceStore();
  featureFlagStore = useFeatureFlagStore();
  themeStore = useThemeStore();

  @Prop({ default: 220 })
  height: number;

  @Prop({ default: 6 })
  initialDisplayMonths: number;

  @Prop({ default: InvestmentCustodian.None })
  investmentCustodian: InvestmentCustodian;

  @Prop()
  displayMonthNames: boolean;

  @Prop({ required: false })
  username: string;

  @Prop({ required: false })
  partnerId: number;

  @Prop({ required: false })
  accountId: number;

  @Prop({ required: false })
  usePerformanceData: boolean;

  get balancesLineChart () {
    return {
      classes: "balances-chart",
      width: 1064,
      height: this.height,
      hasHovers: false
    };
  }

  get selectedStartDate () {
    return moment().subtract((this.initialDisplayMonths), "months");
  }

  get isLoaded (): boolean {
    const apiCallResults = this.investmentDailyBalanceStore.investmentDataIsLoaded && (
            // Don't show HSAB graph until we start collecting daily balances from them
            this.investmentCustodian !== InvestmentCustodian.HsaBank || this.investmentDailyBalanceStore.hasDailyBalances);

    if (this.usePerformanceData) {
      return apiCallResults && this.drivewealthAccountPerformanceStore.accountPerformanceLoaded(this.accountIdentifiers);
    }

    return apiCallResults;
  }

  selectedEndDate = moment();

  mounted () {
    this.investmentDailyBalanceStore.setCurrentEndDateKey(this.selectedEndDate.format("YYYYMMDD"));
    this.loadChartData();
  }

  loadChartData () {
    this.investmentDailyBalanceStore.loadDailyInvestmentBalances(this.loadDailyInvestmentBalancesArgs);
    if (this.usePerformanceData) {
      this.getAccountPerformance();
    }
  }

  get accountIdentifiers (): DrivewealthAccountIdentifierArgs {
    const requestModel: DrivewealthAccountIdentifierArgs = {
      username: this.username,
      partnerId: this.partnerId,
      accountId: this.accountId
    };
    return requestModel;
  }

  getAccountPerformance (): void {
    this.drivewealthAccountPerformanceStore.getAccountPerformance(this.accountIdentifiers);
  }

  get loadDailyInvestmentBalancesArgs () {
    return {
      startDate: this.selectedStartDate.format("YYYY-MM-DD"),
      endDate: this.selectedEndDate.format("YYYY-MM-DD"),
      username: this.username,
      partnerId: this.partnerId,
      accountId: this.accountId,
      investmentCustodian: this.investmentCustodian};
  }

  get chartDataSet (): ChartDataItem[] {
    const accountPerformance = this.drivewealthAccountPerformanceStore.accountPerformanceResult(this.accountIdentifiers);
    let accountPerformanceChartDataItems: ChartDataItem[] = [];
    const earliestPerformanceDataItem = _.head(accountPerformance.performanceReturns);
    if (accountPerformance) {
      accountPerformanceChartDataItems = _.map(accountPerformance.performanceReturns, x => {
        const netContribution = accountPerformance.periodCashFlows + earliestPerformanceDataItem.positionBalance;

        return {
          balance: x.positionBalance,
          date: moment(x.date).format("DD-MMM-YY"),
          netContribution,
          transactionSummaries: null
        };
      });
    }

    return accountPerformanceChartDataItems;
  }

  @Watch("loadDailyInvestmentBalancesArgs")
  reloadDailyBalancesChart () {
    this.loadChartData();
  }
}
